import {Button, Link, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {AdminPinResetDecisionStatus, AdminPinResetRequest} from '@/types';
import {BusinessPinResetRequestStatus} from '@/types';
import * as formatDate from '@/utils/date';

import {pinResetStatusColorScheme} from './pinResetStatus';

type PinResetRequestsTableProps = {
  items: AdminPinResetRequest[];
  canDecide: boolean;
  isDeciding: boolean;
  onDecide: (
    request: AdminPinResetRequest,
    status: AdminPinResetDecisionStatus,
  ) => void;
};

const asDate = (value?: Date | string) =>
  value ? formatDate.DDMonYYYY(String(value)) : '—';

/**
 * Four columns, because the decision is the point of the screen: who asked and
 * when sit under the account they asked for, so the action buttons stay on
 * screen instead of behind a horizontal scroll. The reason they gave shows in
 * the decision dialog rather than costing a column here.
 */
export const PinResetRequestsTable = ({
  items,
  canDecide,
  isDeciding,
  onDecide,
}: PinResetRequestsTableProps) => (
  <Table className="overflow-hidden">
    <Thead>
      <Tr enableHover={false}>
        <Th className="px-4">Business</Th>
        <Th className="px-4">Status</Th>
        <Th className="px-4">Requested</Th>
        <Th className="px-4">Action</Th>
      </Tr>
    </Thead>
    <TBody>
      {items.map((item, index) => {
        const isPending = item.status === BusinessPinResetRequestStatus.Pending;
        return (
          <Tr key={item.id ?? index}>
            <Td className="px-4">
              <Link
                className="font-medium text-cyan-600 hover:underline"
                to={`/merchants/businesses/${item.businessId}`}
              >
                {item.businessName || item.businessId}
              </Link>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {item.requestedByName || item.requestedBy}
                {item.requestedByRole ? ` · ${item.requestedByRole}` : ''}
              </div>
            </Td>
            <Td className="px-4">
              <span
                className={`rounded px-2 py-1 text-xs font-medium ${pinResetStatusColorScheme(
                  item.status,
                )}`}
              >
                {item.status}
              </span>
            </Td>
            <Td className="whitespace-nowrap px-4">
              {asDate(item.createdAt)}
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {isPending ? `Expires ${asDate(item.expiresAt)}` : ''}
              </div>
            </Td>
            <Td className="whitespace-nowrap px-4">
              {isPending && canDecide ? (
                <div className="flex gap-2">
                  <Button
                    colorScheme="green"
                    className="!px-2 !py-1 disabled:opacity-50"
                    disabled={isDeciding}
                    dataCy="approve-pin-reset"
                    onClick={() =>
                      onDecide(item, BusinessPinResetRequestStatus.Approved)
                    }
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    colorScheme="red"
                    className="!px-2 !py-1 disabled:opacity-50"
                    disabled={isDeciding}
                    dataCy="reject-pin-reset"
                    onClick={() =>
                      onDecide(item, BusinessPinResetRequestStatus.Rejected)
                    }
                  >
                    Reject
                  </Button>
                </div>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {isPending ? 'Finance approval required' : 'Decided'}
                </span>
              )}
            </Td>
          </Tr>
        );
      })}
    </TBody>
  </Table>
);
